// Helpers for Microsoft Teams call transcripts.

// A WEBVTT cue timing line, tolerant of real-world variants: optional hours,
// single-digit fields, comma decimals — "00:00:03.120 --> 00:00:05.000",
// "0:03,120 --> 0:05,000".
const CUE_TIMING = /^(?:(\d{1,2}):)?(\d{1,2}):(\d{2})[.,]\d{1,3}\s+-->\s/;

// VTT structural/metadata content that must never render as chat: the file
// header ("WEBVTT", optionally with trailing text) and NOTE/STYLE/REGION blocks.
const METADATA_LINE = /^(WEBVTT|NOTE|STYLE|REGION)\b/;

const pad2 = s => (String(s).length < 2 ? `0${s}` : String(s));

// Teams transcripts arrive as WEBVTT text: a header, then cues — an optional
// identifier line, a "HH:MM:SS.mmm --> HH:MM:SS.mmm" timing line, and the
// spoken line(s). Speaker (when Teams includes it) is a <v Name>…</v> voice
// tag. Parse into {time, speaker, text} cues, keeping ONLY spoken text —
// header, NOTE/STYLE/REGION blocks, identifiers and timing lines are dropped.
export function parseTranscript(raw) {
    const lines = String(raw || "")
        .replace(/\r/g, "")
        .split("\n");
    const cues = [];
    let current = null;

    const flush = () => {
        if (!current) {
            return;
        }
        let text = current.parts.join(" ").trim();
        const voice = text.match(/<v\s+([^>]+)>/);
        const speaker = voice ? voice[1].trim() : null;
        text = text.replace(/<\/?v[^>]*>/g, "").trim();
        if (text) {
            cues.push({ time: current.time, speaker, text });
        }
        current = null;
    };

    lines.forEach(line => {
        const t = line.trim();
        if (!t) {
            // Blank line ends the current cue (VTT block separator).
            flush();
            return;
        }
        const m = t.match(CUE_TIMING);
        if (m) {
            flush();
            current = {
                time: `${pad2(m[1] || 0)}:${pad2(m[2])}:${m[3]}`,
                parts: []
            };
            return;
        }
        if (METADATA_LINE.test(t)) {
            flush();
            return;
        }
        if (current) {
            current.parts.push(t);
        }
        // Outside a cue (header junk, cue-identifier lines right before a
        // timing line, NOTE body text): dropped.
    });
    flush();

    // Fallback: not VTT at all (no timing line anywhere) — treat each
    // blank-line-separated block of plain text as one cue so non-VTT
    // transcripts still display, minus any metadata lines.
    if (!cues.length) {
        String(raw || "")
            .replace(/\r/g, "")
            .split(/\n{2,}/)
            .forEach(block => {
                const text = block
                    .split("\n")
                    .map(l => l.trim())
                    .filter(l => l && !METADATA_LINE.test(l))
                    .join(" ")
                    .replace(/<\/?v[^>]*>/g, "")
                    .trim();
                if (text) {
                    cues.push({ time: null, speaker: null, text });
                }
            });
    }
    return cues;
}

// Consecutive cues by the same speaker become one chat "message group",
// so a run of lines renders as stacked bubbles under a single avatar/name.
export function groupCues(cues) {
    const groups = [];
    cues.forEach(cue => {
        const last = groups[groups.length - 1];
        if (last && last.speaker === cue.speaker) {
            last.cues.push(cue);
        } else {
            groups.push({ speaker: cue.speaker, cues: [cue] });
        }
    });
    return groups;
}
